import type { RouteRecordRaw } from 'vue-router';
import { loadBranding, type Branding } from '@/services/branding.service';

const Index = () => import('@/pages/website/IndexPage.vue');
// Exported so the route table can be asserted by identity: the front decides
// which component the root renders, and a test that cannot compare the loader
// could only check the shape of the route.
export const HomePageLoader = () => import('@/pages/website/HomePage.vue');
export const LoginLoader = () => import('@/pages/website/LoginPage.vue');
const About = () => import('@/pages/website/AboutPage.vue');
const ForgotPassword = () => import('@/pages/website/ForgotPasswordPage.vue');
const ResetPassword = () => import('@/pages/website/ResetPasswordPage.vue');

/**
 * What the route table needs to know about the host it is being served on.
 *
 * `frontMode` is the public front the deployment serves (`hero` = the marketing
 * home, `login` = the marketplace door) and `pathPrefix` tells the apex apart
 * from a brand reached by path.
 */
export type WebsiteFront = Pick<Branding, 'frontMode' | 'pathPrefix'>;

/**
 * Builds the public route table for the front being served.
 *
 * The marketplace door belongs to the platform apex: the root renders the
 * login and the platform's own marketing pages (`/about`, `/specialties`) are
 * not registered at all, so there is nothing to link to them and no way to
 * reach them.
 *
 * A brand reached by path keeps every page, even when the apex is the door:
 * the route table is shared between the apex and the brands (only the SPA
 * history base differs), so removing them would break a brand's own nav.
 */
export function buildWebsiteRoutes(front: WebsiteFront): RouteRecordRaw[] {
  const loginFront = front.frontMode === 'login' && !front.pathPrefix;

  const routes: RouteRecordRaw[] = [
    {
      path: '/',
      name: 'home',
      components: {
        default: loginFront ? LoginLoader : HomePageLoader,
      },
    },
    {
      path: '/agendar',
      name: 'index',
      components: {
        default: Index,
      },
    },
    {
      path: '/index',
      name: 'index2',
      components: {
        default: Index,
      },
    },
  ];

  if (!loginFront) {
    routes.push(
      {
        path: '/about',
        name: 'about',
        components: {
          default: About,
        },
      },
      {
        path: '/specialties',
        name: 'specialties',
        components: {
          default: () => import('@/pages/website/ServicesPage.vue'),
        },
      },
    );
  }

  routes.push(
    {
      path: '/login',
      name: 'login',
      components: {
        default: LoginLoader,
      },
    },
    {
      path: '/forgot-password',
      name: 'forgotPassword',
      components: {
        default: ForgotPassword,
      },
    },
    {
      path: '/reset-password',
      name: 'resetPassword',
      components: {
        default: ResetPassword,
      },
    },
    {
      path: '/tenant/:slug/reserva/:token',
      name: 'atomic-slot-share',
      components: {
        default: () => import('@/pages/website/AtomicSlotPage.vue'),
      },
      props: { default: true },
    },
    {
      path: '/servicios/:slug',
      name: 'service-seo',
      components: {
        default: () => import('@/pages/website/ServiceSeoPage.vue'),
      },
    },
  );


  return routes;
}

export default buildWebsiteRoutes(loadBranding());
